import { format } from 'date-fns'
import { Calendar } from 'lucide-react'

import { Badge } from '@/components/ui/badge'
import type { AdminUser } from '@/hooks/use-admin-users'
import { adminUserSortName } from '@/lib/list-filters/admin-users'
import type { PersonListCardModel } from '@/lib/person-list-card-model'
import {
  formatPlatformGroupLabel,
  getPlatformVisibleGroups,
} from '@/lib/platform-assignable-groups'
import { cn } from '@/lib/utils'

const ADMIN_USER_DATE_FORMAT = 'MMM d, yyyy'

export function adminUserDisplayName(user: AdminUser): string {
  return adminUserSortName(user)
}

export function formatAdminUserDate(iso: string): string {
  return format(new Date(iso), ADMIN_USER_DATE_FORMAT)
}

export function formatAdminUserLastLogin(iso?: string | null): string {
  return iso ? formatAdminUserDate(iso) : 'Never'
}

export function adminUserToPersonModel(user: AdminUser): PersonListCardModel {
  return {
    id: user.id,
    name: adminUserDisplayName(user),
    subtitle: user.email,
    colorSeed: user.id,
  }
}

export function AdminUserStatusBadges({ user }: { user: AdminUser }) {
  return !user.is_active ? (
    <Badge variant="destructive">Inactive</Badge>
  ) : (
    <Badge variant="default">Active</Badge>
  )
}

export function AdminUserPermissionBadges({ user }: { user: AdminUser }) {
  if (user.is_superuser) {
    return (
      <Badge variant="outline" className="text-xs">
        Superuser
      </Badge>
    )
  }
  if (user.is_staff) {
    return (
      <Badge variant="outline" className="text-xs">
        Staff
      </Badge>
    )
  }
  return null
}

type AdminUserAccessBadgesProps = {
  user: AdminUser
  align?: 'start' | 'end'
}

/** Active/inactive status plus staff/superuser chips — shared by user cards and table. */
export function AdminUserAccessBadges({ user, align = 'start' }: AdminUserAccessBadgesProps) {
  return (
    <div className={cn('flex flex-col gap-1', align === 'end' && 'items-end')}>
      <AdminUserStatusBadges user={user} />
      <AdminUserPermissionBadges user={user} />
    </div>
  )
}

export function AdminUserGroupBadges({
  groups,
  maxVisible,
  whenEmpty = 'message',
  className,
}: {
  groups: string[]
  maxVisible?: number
  /** `hidden` — render nothing when no visible groups (compact previews). */
  whenEmpty?: 'message' | 'hidden'
  className?: string
}) {
  const visibleGroups = getPlatformVisibleGroups(groups)
  const displayed =
    maxVisible != null ? visibleGroups.slice(0, maxVisible) : visibleGroups

  if (!displayed.length) {
    if (whenEmpty === 'hidden') return null
    return <span className="text-sm text-muted-foreground">No groups</span>
  }

  return (
    <div className={cn('flex flex-wrap gap-1', className)}>
      {displayed.map((group) => (
        <Badge key={group} variant="outline" className="text-xs">
          {formatPlatformGroupLabel(group)}
        </Badge>
      ))}
    </div>
  )
}

type AdminUserAccountDatesProps = {
  user: AdminUser
  className?: string
}

export function AdminUserAccountDates({ user, className }: AdminUserAccountDatesProps) {
  return (
    <div className={className}>
      <div className="grid grid-cols-1 gap-2 text-xs text-muted-foreground sm:grid-cols-2 sm:gap-4">
        <div className="flex items-center gap-1">
          <Calendar className="size-3 shrink-0" aria-hidden />
          <span>Joined {formatAdminUserDate(user.date_joined)}</span>
        </div>
        <div className="flex items-center gap-1">
          <Calendar className="size-3 shrink-0" aria-hidden />
          <span>Last login {formatAdminUserLastLogin(user.last_login)}</span>
        </div>
      </div>
    </div>
  )
}
